/**
 * Display helpers — branch name formatting, source labels, time formatting,
 * negative-sentiment threshold classification.
 */

const BRAND_PREFIXES = ['Lucky Suki ', 'Lucky BBQ '];

/**
 * Strips the redundant brand prefix from branch names.
 *
 * Every branch name in the seed data starts with "Lucky Suki" or "Lucky BBQ".
 * The brand tag already communicates this, so showing it in the name is noise.
 * Returns the original name if no known prefix matches.
 */
export function stripBrandPrefix(name: string): string {
  for (const prefix of BRAND_PREFIXES) {
    if (name.startsWith(prefix)) {
      return name.slice(prefix.length);
    }
  }
  return name;
}

/** Resolves a design token for APIs that cannot interpret CSS var() strings. */
export function cssVar(name: string): string {
  if (typeof window === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Resolves a design token and applies alpha (0–1), for chart fills that need
 * solid-color derivatives (uPlot fill, SVG strokes). Understands #RGB/#RRGGBB
 * and rgb() values; falls back to the raw resolved string when unparseable.
 */
export function cssVarRgba(name: string, alpha: number): string {
  const raw = cssVar(name);
  if (!raw) return '';
  let r: number, g: number, b: number;
  const hex = raw.replace('#', '');
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16); g = parseInt(hex[1] + hex[1], 16); b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16); g = parseInt(hex.slice(2, 4), 16); b = parseInt(hex.slice(4, 6), 16);
  } else {
    const m = raw.match(/^rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (!m) return raw;
    r = Number(m[1]); g = Number(m[2]); b = Number(m[3]);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Negative-sentiment percentage threshold classification.
 * <5% = low (green), 5–15% = mid (yellow), >15% = high (red).
 *
 * Returns a BEM-safe class suffix for use in component-scoped class binding.
 * Returns `null` when `pct` is null/undefined — callers MUST null-check before
 * interpolating the result into a CSS class string (e.g. guard with `v-if`).
 */
export type NegThreshold = 'low' | 'mid' | 'high';

export function negThresholdClass(pct: number | null): NegThreshold | null {
  if (pct == null) return null;
  if (pct < 5) return 'low';
  if (pct <= 15) return 'mid';
  return 'high';
}

/**
 * Formats a number with Thai-style compact notation.
 * Values >= 1M → "1.2M", >= 1k → "1.2k", null/undefined → "—".
 */
export function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/**
 * Formats a rating value for display.
 * null/undefined → "—", otherwise → "4.2".
 */
export function fmtRating(r: number | null | undefined): string {
  if (r == null) return '—';
  return r.toFixed(1);
}

/**
 * Formats a negative-percentage value for display.
 * null/undefined → "—", otherwise → "12.5%".
 */
export function fmtNegativePct(p: number | null | undefined): string {
  if (p == null) return '—';
  return `${p.toFixed(1)}%`;
}

/**
 * Rune-aware text truncation for Thai / multi-byte text.
 * Appends '…' when the text exceeds maxRunes characters.
 */
export function truncateText(text: string, maxRunes: number): string {
  if (!text) return '';
  const runes = [...text];
  if (runes.length <= maxRunes) return text;
  return runes.slice(0, maxRunes).join('') + '…';
}

/**
 * Formats a baht amount for display with Thai locale thousands separators.
 * Non-finite values → "—".
 */
export function fmtBaht(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return Math.round(n).toLocaleString('th-TH');
}

/**
 * Parses a PocketBase autodate string ("YYYY-MM-DD HH:MM:SS.sssZ") into a Date.
 * Returns null on failure. PocketBase emits a space instead of a T between date
 * and time — this normalizes before parsing.
 */
function parseTs(s: string | null): Date | null {
  if (!s) return null;
  const iso = s.indexOf(' ') === 10 ? s.replace(' ', 'T') : s;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Formats a PocketBase timestamp into a Thai-locale, ICT-zoned string.
 * Suitable for in-cell display in tables; returns "—" on null/invalid input.
 */
export function fmtICT(s: string | null, opts?: Intl.DateTimeFormatOptions): string {
  const d = parseTs(s);
  if (!d) return '—';
  return d.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
    ...opts,
  });
}

/**
 * Formats a duration (in seconds) as a human-readable Thai age string.
 * Returns "—" on null input.
 */
export function ageHuman(sec: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${Math.round(sec)} วินาที`;
  if (sec < 3600) return `${Math.round(sec / 60)} นาที`;
  if (sec < 86400) return `${Math.round(sec / 3600)} ชม.`;
  return `${Math.round(sec / 86400)} วัน`;
}

/**
 * Formats a duration (in seconds) as a Thai "Xนาที Yวิ" string.
 * Returns "—" on null input.
 */
export function durHuman(sec: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec} วินาที`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}นาที ${s}วิ` : `${m} นาที`;
}

/**
 * Thai relative-time formatter. Accepts an ISO-8601 string and returns a
 * human-readable Thai string.
 *
 * Options:
 *  - precision: 'day' (default) or 'minute' for finer granularity.
 *  - prefix: string prepended to the result (e.g. "ล่าสุด ").
 */
export function timeAgo(
  iso: string | null | undefined,
  opts?: { precision?: 'minute' | 'day'; prefix?: string },
): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  const prefix = opts?.prefix ?? '';
  if (opts?.precision === 'minute') {
    if (diffMin < 1) return `${prefix}เมื่อสักครู่`;
    if (diffMin < 60) return `${prefix}${diffMin} นาทีที่แล้ว`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${prefix}${diffHr} ชม. ที่แล้ว`;
    return `${prefix}${Math.floor(diffHr / 24)} วันที่แล้ว`;
  }
  // day precision (default)
  const diffDays = Math.round(diffMin / 1440);
  if (diffDays < 1) return `${prefix}วันนี้`;
  if (diffDays === 1) return `${prefix}1 วันที่แล้ว`;
  if (diffDays < 30) return `${prefix}${diffDays} วันที่แล้ว`;
  return d.toLocaleDateString('th-TH', { month: 'short', day: 'numeric' });
}
